import React, { useMemo } from "react";
import ReactEChartsCore from "echarts-for-react/lib/core";
import * as echarts from "echarts/core";
import { LineChart as ELineChart } from "echarts/charts";
import {
  GridComponent,
  TooltipComponent,
  AxisPointerComponent,
  TitleComponent,
  LegendComponent,
  DataZoomComponent,
  AriaComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import { useChartTheme, withChartTheme } from "./theme";

echarts.use([
  TitleComponent,
  TooltipComponent,
  AxisPointerComponent,
  LegendComponent,
  DataZoomComponent,
  GridComponent,
  AriaComponent,
  ELineChart,
  CanvasRenderer,
]);

/**
 * HomeLineChart – polished UX version
 * Props:
 *  - title, labels, series
 *  - colors?: string[]
 *  - height?: number | string
 *  - unit?: string (fallback unit for single y-axis)
 *  - yAxes?: [{ name, unit, color, position, min, max, splitLine }]
 *  - legendPosition?: 'top'|'bottom'|'left'|'right'
 *  - legendOrient?: 'horizontal'|'vertical'
 *  - showLegend?: boolean
 *  - showZoom?: boolean
 *  - smooth?: boolean
 *  - yMinBuffer?: number | number[] 
 *  - labelsPosition?: 'bottom'|'top'
 *  - labelRotate?: number
 *  - labelFormatter?: (val, idx) => string
 *  - theme?: 'light'|'dark'|string
 *  - onPointClick?: (params) => void
 *  - loading?: boolean
 *  - emptyMessage?: string
 */
function HomeLineChart(props) {
  const chartTheme = useChartTheme();
  const {
    title,
    labels = [],
    series = [],
    unit,
    yMinBuffer = 5, // can be number or [numberForAxis0, numberForAxis1, ...]
    smooth = true,
    showLegend = true,
    showZoom = true,
    colors = ["#5a6fa3", "#b5d56a", "#ff9f43", "#54a0ff", "#ee5253"],
    height = 320,
    theme,
    onPointClick,
    labelsPosition = "bottom",
    labelRotate = 0,
    labelFormatter = null,
    yAxes = undefined,
    legendPosition = "top", // NEW: control legend spot
    legendOrient = "horizontal",
    loading = false,
    emptyMessage = "No data to display",
  } = props;

  const isEmpty =
    !Array.isArray(series) ||
    series.length === 0 ||
    series.every((s) => !s?.data || s.data.length === 0);

  const option = useMemo(() => {
    if (isEmpty) {
      // Minimal config just to show empty grid background (for visual consistency)
      return {
        title: title
          ? { text: title, left: "left", textStyle: { fontSize: 15 } }
          : undefined,
        grid: { left: 48, right: 16, top: 28, bottom: 36, containLabel: true },
        xAxis: {
          type: "category",
          data: labels,
          axisLine: { show: true },
          axisTick: { show: true },
          axisLabel: { show: true },
        },
        yAxis: { type: "value", splitLine: { show: true } },
        series: [],
        aria: { enabled: true },
      };
    }

    const hasArea = series.some((s) => !!s.area);
    const hasUserMultipleY = Array.isArray(yAxes) && yAxes.length > 1;

    // area gradient helper
    const makeAreaStyle = (c) => ({
      color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
        { offset: 0, color: echarts.color.modifyHSL(c, 0, 0, +0.1) },
        { offset: 1, color: echarts.color.modifyHSL(c, 0, 0, -0.8) },
      ]),
      opacity: 0.2,
    });

    // Final ECharts series
    const echartsSeries = (series || []).map((s, idx) => {
      const color = colors[idx % colors.length];
      const yAxisIndex =
        typeof s.yAxisIndex === "number"
          ? s.yAxisIndex
          : s.axis === "right"
          ? 1
          : 0;

      return {
        name: s.name,
        type: s.type || "line",
        data: s.data,
        smooth: typeof s.smooth === "boolean" ? s.smooth : smooth,
        step: s.step,
        lineStyle: { width: 2, ...(s.lineStyle || {}) },
        symbol: s.symbol ?? "circle",
        showSymbol: typeof s.showSymbol === "boolean" ? s.showSymbol : false,
        symbolSize: s.symbolSize ?? 6,
        emphasis: s.emphasis ?? { focus: "series" },
        yAxisIndex,
        // area fill if requested
        areaStyle: s.area ? makeAreaStyle(color) : undefined,
        itemStyle: { color },
      };
    });

    // helper to obtain buffer for axis idx

        // helper to obtain buffer for axis idx
    const bufferForAxis = (idx) => {
      if (Array.isArray(yMinBuffer))
        return Number.isFinite(yMinBuffer[idx])
          ? yMinBuffer[idx]
          : Number.isFinite(yMinBuffer[0])
          ? yMinBuffer[0]
          : 5;
      return typeof yMinBuffer === "number" ? yMinBuffer : 5;
    };
    // yAxis build
 // yAxis build — changed so min always starts at 0 (floor) and yMinBuffer is applied to the TOP (max padding)
    const builtYAxes =
      Array.isArray(yAxes) && yAxes.length
        ? yAxes.map((ya, idx) => {
            const isIntegerAxis =
              ya?.integer === true ||
              /\b(alert|count|items|num|integer|alerts)\b/i.test(
                `${ya?.unit ?? ""} ${ya?.name ?? ""}`
              );

            const buf = bufferForAxis(idx);

            return {
              type: "value",
              name: ya?.name ?? "",
              position: ya?.position ?? (idx === 1 ? "right" : "left"),
              // min: default to 0 unless explicit min provided
              min: typeof ya?.min === "number" ? ya.min : 0,
              // max: add buffer to computed max (so buffer is top padding, not subtracting min)
              max:
                typeof ya?.max === "number"
                  ? ya.max
                  : (value) => {
                      if (!value || typeof value.max !== "number") return undefined;
                      const v = Math.ceil(value.max + (typeof buf === "number" ? buf : 0));
                      return Number.isFinite(v) ? v : undefined;
                    },
              axisLine: { show: true, lineStyle: { color: ya?.color ?? "#888" } },
              axisTick: { show: true },
              // Force integer ticks for count/alert axes
              minInterval: isIntegerAxis ? 1 : ya?.minInterval,
              axisLabel: {
                color: ya?.color ?? "#444",
                formatter: (v) =>
                  isIntegerAxis ? String(Math.round(v)) : ya?.unit ? `${v} ${ya.unit}` : `${v}`,
              },
              splitLine: {
                show: ya?.splitLine ?? true,
                lineStyle: { color: "#eee" },
              },
              axisPointer: { label: { show: true } },
            };
          })
        : [
            {
              type: "value",
              min: 0,
              max: (value) => {
                if (!value || typeof value.max !== "number") return undefined;
                const v = Math.ceil(value.max + (typeof bufferForAxis(0) === "number" ? bufferForAxis(0) : 0));
                return Number.isFinite(v) ? v : undefined;
              },
              axisLine: { show: true, lineStyle: { color: "#888" } },
              axisTick: { show: true },
              axisLabel: { show: true, color: "#444", formatter: (v) => (unit ? `${v} ${unit}` : `${v}`) },
              splitLine: { show: true, lineStyle: { color: "#eee" } },
              boundaryGap: [0, "10%"],
              axisPointer: { label: { show: true } },
            },
          ];
  //   const builtYAxes =
  //     Array.isArray(yAxes) && yAxes.length
  //       ? yAxes.map((ya, idx) => ({
  //           type: "value",
  //           name: ya?.name ?? "",
  //           position: ya?.position ?? (idx === 1 ? "right" : "left"),
  //           // min:
  //           //   typeof ya?.min === "number"
  //           //     ? ya.min
  //           //     : (value) => {
  //           //         // auto-min with buffer
  //           //         if (!value || typeof value.min !== "number") return undefined;
  //           //         const v = Math.floor(value.min - bufferForAxis(idx));

  //           //         return Number.isFinite(v) ? v : undefined;
  //           //       },
  //           min:
  // typeof ya?.min === "number"
  //   ? ya.min
  //   : (value) => {
  //       // auto-min with per-axis buffer (changed behavior: buffer is treated as a lower bound)
  //       if (!value || typeof value.min !== "number") return undefined;

  //       const buf = bufferForAxis(idx);
  //       const raw = Math.floor(value.min - buf);

  //       // Ensure we don't return a negative percent axis and honor buffer as a minimum floor.
  //       // If axis explicitly has unit '%' or explicit min === 0, clamp to >= 0.
  //       let finalMin = raw;

  //       if (typeof buf === "number" && buf > 0) {
  //         // Treat positive buffer as a lower bound (user expects +buffer, not subtraction into negative)
  //         finalMin = Math.max(finalMin, buf);
  //       }

  //       if (ya?.unit === "%" || ya?.min === 0) {
  //         finalMin = Math.max(finalMin, 0);
  //       }

  //       return Number.isFinite(finalMin) ? finalMin : undefined;
  //     },
  //           max: typeof ya?.max === "number" ? ya.max : undefined,
  //           axisLine: { show: true, lineStyle: { color: ya?.color ?? "#888" } },
  //           axisTick: { show: true },
  //           axisLabel: {
  //             color: ya?.color ?? "#444",
  //             formatter: (v) => (ya?.unit ? `${v} ${ya.unit}` : `${v}`),
  //           },
  //           splitLine: {
  //             show: ya?.splitLine ?? true,
  //             lineStyle: { color: "#eee" },
  //           },
  //           axisPointer: { label: { show: true } },
  //         }))
  //       : [
  //           {
  //             type: "value",
  //             min: (value) => {
  //               if (!value || typeof value.min !== "number") return undefined;
  //               const v = Math.floor(value.min - bufferForAxis(0));
  //               return Number.isFinite(v) ? v : undefined;
  //             },
  //             axisLine: { show: true, lineStyle: { color: "#888" } },
  //             axisTick: { show: true },
  //             axisLabel: {
  //               show: true,
  //               color: "#444",
  //               formatter: (v) => (unit ? `${v} ${unit}` : `${v}`),
  //             },
  //             splitLine: { show: true, lineStyle: { color: "#eee" } },
  //             boundaryGap: [0, "10%"],
  //             axisPointer: { label: { show: true } },
  //           },
  //         ];

    // GRID: add extra space if legend bottom or right y-axis
    const grid = {
      left: 56,
      right: hasUserMultipleY ? 56 : 16,
      top: hasArea ? 48 : 36,
      bottom: showLegend && legendPosition === "bottom" ? 40 : showZoom ? 56 : 40,
      containLabel: true,
    };

    // Legend positioning map
    const legendPos = (() => {
      switch (legendPosition) {
        case "bottom":
          return { bottom: 6, left: "center", orient: legendOrient };
        case "left":
          return { left: 8, top: 8, orient: "vertical" };
        case "right":
          return { right: 8, top: 8, orient: "vertical" };
        default:
          // 'top'
          return { top: 8, left: "center", orient: legendOrient };
      }
    })();

    return {
      color: colors,
      aria: { enabled: true },
      title: title
        ? { text: title, left: "left", textStyle: { fontSize: 13 } }
        : undefined,
      tooltip: {
        trigger: "axis",
        appendToBody: true,
        confine: true,
        axisPointer: { type: "line" },
        // FIX: Use series.yAxisIndex to pick correct unit from yAxes
        formatter: function (params) {
          if (!Array.isArray(params) || params.length === 0) return "";
          const xLabel = params[0]?.axisValueLabel ?? "";
          const rows = params.map((p) => {
            // const yIdx = typeof p?.seriesId === "string"
            //   ? p.seriesId // not reliable, so fallback to seriesIndex mapping
            //   : undefined;

            const safeYAxisIndex =
              typeof p?.seriesIndex === "number"
                ? (echartsSeries[p.seriesIndex]?.yAxisIndex ?? 0)
                : 0;

            let suffix = "";
            if (Array.isArray(yAxes) && yAxes.length) {
              const cfg = yAxes[safeYAxisIndex];
              if (cfg?.unit) suffix = ` ${cfg.unit}`;
            } else if (unit) {
              suffix = ` ${unit}`;
            }
            const val = p?.value ?? "";
            return `<div>${p.marker} ${p.seriesName}: <strong>${val}${suffix}</strong></div>`;
          });
          return `<div style="min-width:160px"><div style="margin-bottom:4px;color:#666"> ${xLabel}</div>${rows.join(
            ""
          )}</div>`;
        },
      },
      legend: showLegend
        ? {
            ...legendPos,
            textStyle: { fontSize: 15, color: "#444" },
            itemWidth: 10,
            itemHeight: 10,
            selector: false,
          }
        : undefined,
      grid,
      xAxis: {
        type: "category",
        data: labels,
        position: labelsPosition,
        axisLabel: {
          rotate: labelRotate,
          formatter:
            typeof labelFormatter === "function" ? labelFormatter : undefined,
          overflow: "truncate",
          // Let ECharts decide based on width
          interval: "auto",
          color: "#444",
        },
        axisLine: { show: true, lineStyle: { color: "#ccc" } },
        axisTick: { show: true, alignWithLabel: true },
      },
      yAxis: builtYAxes,
    //   dataZoom: showZoom
    //     ? [
    //         { type: "inside", start: 0, end: 100 },
    //         { type: "slider", start: 0, end: 100, bottom: legendPosition === "bottom" ? 28 : 0 },
    //       ]
    //     : undefined,
      axisPointer: { link: [{ xAxisIndex: "all" }] },
      series: echartsSeries,
      animation: true,
    };
  }, [
    title,
    labels,
    series,
    unit,
    yMinBuffer,
    smooth,
    showLegend,
    showZoom,
    colors,
    labelsPosition,
    labelRotate,
    labelFormatter,
    yAxes,
    legendPosition,
    legendOrient,
    isEmpty,
  ]);

  const onEvents = useMemo(
    () => ({
      click: (params) => {
        if (onPointClick) onPointClick(params);
      },
    }),
    [onPointClick]
  );

  // Simple empty state overlay
  const renderOverlay = () =>
    isEmpty ? (
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#666",
          fontSize: 13,
        }}
      >
        {emptyMessage}
      </div>
    ) : null;

  const themedOption = useMemo(

    () => withChartTheme(option, chartTheme),

    [option, chartTheme]

  );


  return (
    <div
      className="chart-container"
      style={{ position: "relative", width: "100%", height: typeof height === "number" ? `${height}px` : height }}
    >
      <ReactEChartsCore
        echarts={echarts}
        option={themedOption}
        notMerge={true}
        lazyUpdate={true}
        onEvents={onEvents}
        theme={theme}
        style={{ width: "100%", height: "100%" }}
        showLoading={!!loading}
        opts={{ renderer: "canvas" }}
      />
      {renderOverlay()}
    </div>
  );
}

export default HomeLineChart;
